import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  ArrowUpDown,
  Grid3x3,
  Loader2,
  Printer,
  Settings2,
  Timer,
  Users,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useToast } from '@/components/ui/use-toast';
import RoundCountdown from '../../components/competition/RoundCountdown';
import StartRoundDialog from '../../components/competition/StartRoundDialog';
import { BoulderTally } from '../../components/competition/BoulderTally';
import { LeadCardSummary } from '../../components/competition/LeadCardSummary';
import LiveLeadCardDialog from '../components/LiveLeadCardDialog';
import LiveScoreCardDialog from '../components/LiveScoreCardDialog';
import RoundBlocksTable from '../components/RoundBlocksTable';
import RoundRoutesTable from '../components/RoundRoutesTable';
import ScoreboardFilter from '../components/ScoreboardFilter';
import { HeaderActions } from '../layout/header-actions';
import { matchesFilter } from '../scoreboard-filter';
import { competitionRoundsService } from '../../services/competition-rounds';
import {
  roundScoresService,
  withSavedScore,
  type RankingInfo,
  type RoundScore,
  type RoundScoreClimb,
  type ScoreCategory,
  type ScoreJudge,
} from '../../services/round-scores';
import { refreshCompetitionRounds } from '../hooks/useCompetitionRounds';
import { useCurrentLocation } from '../hooks/useCurrentLocation';
import { useStickySearchParam } from '../hooks/useStickySearchParam';
import { useGymAdmin } from '../context/gym-admin-context';
import { judgedIds, rankingAttempts, summarise, tally } from '../../utils/round-score-summary';
import type { LeadClassStyles } from '../../components/competition/lead-climb-style';
import { errorCodeOf } from '../../services/api-error';
import { TiebreakMark } from '../../components/competition/TiebreakMark';
import { ROUND_STATUSES, STATUS_VARIANT } from '../round-status';
import type { CompetitionRound, RoundStatus } from '../../types/competition-round';

/** The columns a table of entrants can be put in order by. */
type SortKey = 'rank' | 'name' | 'surname' | 'team';

/** Which way round the round is read: by entrant, or by problem. */
type BoardView = 'athletes' | 'blocks';

/** The two ways, in the order the switch offers them, with their glyphs. */
const BOARD_VIEWS: { value: BoardView; icon: typeof Users }[] = [
  { value: 'athletes', icon: Users },
  { value: 'blocks', icon: Grid3x3 },
];

/** Sorts an entrant with no position yet behind everybody who has one. */
const UNRANKED = Number.MAX_SAFE_INTEGER;

/** What a board's places come from until the server has said. */
const STANDARD_RANKING: RankingInfo = { strategy: 'standard', judgedBlocks: false };

/**
 * The start refusals that name something somebody has to go and do — a lead
 * route with no holds counted, a class short of judged blocks (Art. 11 of the
 * OPES regulation) — and get a dialog rather than a toast.
 */
const NAMED_START_REFUSALS = ['routes_missing_holds', 'judged_blocks_missing'];

/**
 * One round's scoreboard — a page of its own, and the one the panel is left
 * open on all day.
 *
 * One tab per class the round runs, and **the same round read either way
 * round**: by athlete, one row per entrant with their whole card behind a
 * click (one line per block, the same buttons the per-block table has — every
 * press saved there and then, and the row behind redrawn when the card is
 * closed, see `LiveScoreCardDialog`); or by block, one problem per field with
 * everybody on it, which is the live surface somebody uses standing at the wall
 * (`RoundBlocksTable`, the same table `/blocks` gives a judge). The switch is
 * beside the class tabs, because which way the table reads is a way of looking
 * at one round, not a different screen — whoever runs the round sets the blocks
 * up, hands them to judges and corrects a card without ever leaving this page.
 *
 * **This is the round's own page** (`/rounds/:roundId`), and the setup behind it
 * is the one with a word on the end (`/edit`): the two are read at different
 * times — the setup is settled before the clock starts and then never touched
 * again, while this is the screen of the day, reached in one click from the menu
 * — so the shorter URL is this one's, and the menu's row opens it.
 *
 * **The round's clock is run from here**, beside its state: starting it, stopping
 * it and closing it are decisions taken with this board open, not on the page
 * where the round was set up a week earlier. Only the states the server would
 * actually move the round to can be picked (`available_statuses`, straight out of
 * `Lifecycle::Round`), so the panel keeps no copy of the transition table and a
 * round with nothing on the wall is never offered "running". Each move still goes
 * through the endpoint that carries what it needs: starting asks how long for,
 * going back to not-started asks about the results, closing says what it ends.
 *
 * The open class and the typed filter live in the URL, so a shared link and a
 * reload land on the same view.
 */
export default function RoundScoresPage() {
  const { roundId } = useParams<{ roundId: string }>();
  const navigate = useNavigate();
  const { placePath, hasAnyRole } = useGymAdmin();
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { location: competition, loading: competitionLoading, slug } = useCurrentLocation();

  const [round, setRound] = useState<CompetitionRound | null>(null);
  const [scores, setScores] = useState<RoundScore[]>([]);
  const [climbs, setClimbs] = useState<RoundScoreClimb[]>([]);
  /** The classes this round runs, which is to say the tabs. */
  const [categories, setCategories] = useState<ScoreCategory[]>([]);
  /** The competition's judges, for handing a judge-only block to some of them. */
  const [judges, setJudges] = useState<ScoreJudge[]>([]);
  /** The regulation the server placed the board by. */
  const [ranking, setRanking] = useState<RankingInfo>(STANDARD_RANKING);
  const [loading, setLoading] = useState(true);
  /**
   * Which round everything above belongs to. The menu moves between rounds
   * without unmounting the page, so for the renders between the move and the
   * new board arriving, the tabs on screen are still the round just left —
   * and anything that reads them has to know that before it acts on them.
   */
  const [loadedRoundId, setLoadedRoundId] = useState<string | null>(null);

  // The class, the filter and which way the table reads: in the URL, so the
  // link somebody sends from the wall is the view they were looking at, and
  // remembered under this round for the rest of the session, so stepping off
  // the board and back comes back to where the desk left it. Under *this*
  // round — another round's classes are its own.
  const board = `round-${roundId ?? ''}`;
  const [activeTab, setActiveTab] = useStickySearchParam(board, 'category');
  const [filter, setFilter] = useStickySearchParam(board, 'q');
  const [viewParam, setView] = useStickySearchParam(board, 'view', 'athletes');
  const view: BoardView = viewParam === 'blocks' ? 'blocks' : 'athletes';

  // Which blocks need a judge is decided by whoever runs the round. The page
  // is theirs anyway, but the table is shared with the judges' own screen.
  const canSetBlocks = hasAnyRole(['admin', 'editor']);
  const [sortKey, setSortKey] = useState<SortKey>('surname');
  const [sortAsc, setSortAsc] = useState(true);

  const [editing, setEditing] = useState<RoundScore | null>(null);

  /** Which move of the round's is in flight, if any. */
  const [busy, setBusy] = useState<string | null>(null);
  const [startOpen, setStartOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  // Closing shuts the member app out of the round for good, so it is asked for
  // rather than done on one tap.
  const [closeOpen, setCloseOpen] = useState(false);
  // Moving the round to its check, or back to the athletes' corrections,
  // changes who writes on every card in it: asked for too, and says who.
  const [statusMove, setStatusMove] = useState<'verification' | 'corrections' | null>(null);
  const [clearResults, setClearResults] = useState(false);
  /**
   * Why a start was refused, when it was refused for something somebody has to
   * go and do: a lead round whose routes nobody has counted the holds on, or a
   * class with too few judged blocks for the regulation.
   *
   * Deliberately not a greyed-out button. The round *is* startable as far as its
   * own shape goes — it has routes on the wall and classes to climb them — and
   * the thing that is missing is a measurement somebody forgot, which has to be
   * named to be fixed. So the toggle stays live, the server refuses the move
   * (`routes_missing_holds`), and this says which routes.
   */
  const [startRefusal, setStartRefusal] = useState<string | null>(null);

  const load = useCallback(
    async (competitionSlug: string, id: string) => {
      setLoading(true);
      try {
        const [loadedRound, page] = await Promise.all([
          competitionRoundsService.getRound(competitionSlug, id),
          roundScoresService.list(competitionSlug, id),
        ]);
        setRound(loadedRound);
        setScores(page.scores);
        setClimbs(page.climbs);
        setCategories(page.categories);
        setJudges(page.judges);
        setRanking(page.ranking);
        setLoadedRoundId(id);
      } catch (error) {
        toast({
          title: t('gymAdmin.roundScores.loadError'),
          description: error instanceof Error ? error.message : t('errors.unexpectedError'),
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    },
    [t, toast]
  );

  useEffect(() => {
    if (slug && roundId) load(slug, roundId);
  }, [slug, roundId, load]);

  /**
   * The round's state again, and nothing else: no skeleton, no board refetch.
   *
   * This is the screen the desk leaves open all day, so the round may well have
   * been closed somewhere else — the other half of the panel, another laptop —
   * while it sat there. The server refuses the card either way
   * (`Api::Competitions::RoundScoresController`), and this is what lets the page
   * then agree with it and put its own buttons away, rather than going on
   * offering a save that will not happen until somebody reloads.
   */
  const refreshRound = useCallback(async () => {
    if (!slug || !roundId) return;

    try {
      setRound(await competitionRoundsService.getRound(slug, roundId));
    } catch {
      // The refused save has already said what went wrong; there is nothing to
      // add about having failed to ask a second question.
    }
  }, [slug, roundId]);

  /** Where the round may go from here, as the server's machine answered. */
  const available = useMemo(
    () => new Set(round?.available_statuses ?? []),
    [round?.available_statuses]
  );

  /**
   * One move of the round's, and the board redrawn after it. The table is
   * refetched rather than left alone because a reset can take the results with
   * it — and silently, without the skeleton: this screen is being watched.
   */
  const runAction = async (key: string, action: () => Promise<CompetitionRound>) => {
    if (!slug || !round) return;

    setBusy(key);
    try {
      setRound(await action());
      const page = await roundScoresService.list(slug, round.id);
      setScores(page.scores);
      setClimbs(page.climbs);
      setCategories(page.categories);
      setJudges(page.judges);
      setRanking(page.ranking);
      // The menu lists the rounds with their state, so it is told too.
      refreshCompetitionRounds();
    } catch (error) {
      // A start refused for something somebody has to go and do gets a dialog
      // naming it, not a toast that slides away while they read it.
      if (NAMED_START_REFUSALS.includes(errorCodeOf(error) ?? '')) {
        setStartRefusal(error instanceof Error ? error.message : t('errors.unexpectedError'));
        return;
      }

      toast({
        title: t('errors.actionFailed'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setBusy(null);
    }
  };

  /**
   * A state picked off the toggle, made through the endpoint that carries what
   * that move needs: how long to run for, and whether the results go with the
   * clock. Stopping the clock is the one move that needs nothing said about it,
   * so it is the one that happens on the tap. Going to the check and back
   * changes who writes, so each says so before it happens, through the generic
   * status door.
   */
  const requestStatus = (target: RoundStatus) => {
    if (!slug || !round || target === round.status) return;

    if (target === 'active') return setStartOpen(true);
    if (target === 'draft') return setResetOpen(true);
    if (target === 'closed') return setCloseOpen(true);
    if (target === 'verification') return setStatusMove('verification');
    if (target === 'corrections' && round.status === 'verification') return setStatusMove('corrections');

    runAction('finish', () => competitionRoundsService.finishRound(slug, round.id));
  };

  // The open tab has to be one the round actually runs: a stale link would
  // otherwise leave the board pointed at nothing.
  //
  // Only ever judged against *this* round's classes, which is why the question
  // is not merely whether a load is in flight. Coming back to a round the menu
  // left earlier, the remembered class is restored on the first render while
  // the classes in hand are still the other round's — and a tab this round runs
  // perfectly well would be thrown away as one it does not.
  useEffect(() => {
    if (loading || loadedRoundId !== roundId) return;
    if (categories.some((category) => category.id === activeTab)) return;
    setActiveTab(categories[0]?.id ?? '');
  }, [loading, loadedRoundId, roundId, categories, activeTab, setActiveTab]);

  // Tops and zones are a boulder round's currency; a lead round's is how far up
  // the route and how long it took. Both have standings, and the server works
  // out both.
  const isBoulder = round?.round_type === 'Boulder';
  const isLead = round?.round_type === 'Lead';

  /**
   * How each class climbs this round, by class id. The tables below put it
   * together with each route's own forcing to decide what a card's switch
   * offers (`permittedLeadStyles`) — the panel never keeps a second copy of the
   * rule.
   */
  const classStyles = useMemo<LeadClassStyles>(
    () => Object.fromEntries(categories.map((category) => [category.id, category.climb_style])),
    [categories]
  );

  const inTab = useMemo(
    () => (activeTab ? scores.filter((s) => s.category_id === activeTab) : scores),
    [scores, activeTab]
  );

  /**
   * The typed filter, against whatever the desk has in its head about a row:
   * a name, a surname, a squad. Standings are still worked out over the whole
   * class — a filtered-down table must not renumber anybody.
   */
  const shown = useMemo(
    () =>
      inTab.filter((score) =>
        matchesFilter(filter, score.name, score.surname, score.full_name, score.team_name)
      ),
    [inTab, filter]
  );

  /**
   * Where each entrant of the open class stands, as the server placed them —
   * over the whole class, so a filtered-down table renumbers nobody. Each class
   * is its own competition, so a position only means something on its own tab.
   */
  const places = useMemo(
    () => new Map(inTab.filter((score) => score.rank != null).map((score) => [score.id, score.rank as number])),
    [inTab]
  );

  /** The judged blocks, whose share the "solved" cell shows under OPES. */
  const judged = useMemo(() => judgedIds(climbs), [climbs]);

  /** Whether the round has standings at all — both disciplines do. */
  const ranked = isBoulder || isLead;

  /**
   * Whether anybody in the open class has been judged on anything yet. Until
   * somebody has, the two result columns say nothing, and the table is really a
   * start list — which is what it prints as.
   *
   * What counts as "something on the card" is the discipline's: a go on a
   * boulder, a hold reached on a lead route.
   */
  const hasResults = useMemo(
    () =>
      inTab.some((score) =>
        score.cards.some((card) =>
          isLead ? card.holds_count != null : card.attempts.length > 0
        )
      ),
    [inTab, isLead]
  );

  const visible = useMemo(() => {
    const direction = sortAsc ? 1 : -1;

    if (sortKey === 'rank') {
      return [...shown].sort(
        (a, b) => ((places.get(a.id) ?? UNRANKED) - (places.get(b.id) ?? UNRANKED)) * direction
      );
    }

    const field = (score: RoundScore) =>
      (sortKey === 'name' ? score.name : sortKey === 'surname' ? score.surname : score.team_name) ??
      '';

    return [...shown].sort(
      (a, b) => field(a).localeCompare(field(b), undefined, { sensitivity: 'base' }) * direction
    );
  }, [shown, places, sortKey, sortAsc]);

  const sortBy = (key: SortKey) => {
    if (key === sortKey) return setSortAsc((current) => !current);
    setSortKey(key);
    setSortAsc(true);
  };

  const sortableHeader = (
    key: SortKey,
    label: string,
    options: { title?: string; className?: string } = {}
  ) => (
    <th className={cn('px-3 py-2 text-left', options.className)}>
      <button
        type="button"
        onClick={() => sortBy(key)}
        title={options.title}
        className="inline-flex items-center gap-1 font-medium hover:text-foreground"
        data-testid={`sort-${key}`}
      >
        {label}
        {/* The arrow is a handle, not information: it has no business on a
            printed sheet. */}
        <ArrowUpDown
          className={cn('size-3 print:hidden', sortKey !== key && 'opacity-30')}
          aria-hidden
        />
      </button>
    </th>
  );

  if (competitionLoading || loading) return <Skeleton className="h-96 w-full max-w-6xl" />;
  if (!competition || !slug || !round) return null;

  const openCategory = categories.find((category) => category.id === activeTab);

  /**
   * Whether this board still writes. A round ends twice, and the second ending
   * ends everybody's turn — this page included: the server takes nothing more
   * on a closed round, from anybody (see Lifecycle::Round). Verification is the
   * other half and stays open, because putting the cards straight is what it is
   * for.
   *
   * Read off `closed` rather than `status`, which lags a round whose clock ran
   * out a moment ago.
   */
  const scoringOpen = !round.closed;

  /**
   * Nobody judged yet, so what prints is a start list rather than a scoreboard:
   * the two result columns come off the sheet — they would be a stripe of
   * dashes — and their half of the page is given over to the judge to write on.
   *
   * Both columns stay on screen. The "solved" cell is the way into the
   * correction dialog, and taking it away would leave nobody able to enter a
   * card by hand before the first one arrives from a judge.
   */
  const startList = !hasResults;
  const resultColumn = startList ? 'print:hidden' : undefined;

  return (
    <div className="max-w-6xl space-y-6">
      {/* The way across to the round's own page: its name, its clock, and the
          classes and blocks it runs. Up in the header, which is the one row that
          stays put however far down the board somebody has scrolled. */}
      <HeaderActions>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => navigate(placePath(`/rounds/${round.id}/edit`))}
          data-testid="open-round-setup"
        >
          <Settings2 className="size-4" aria-hidden />
          {t('gymAdmin.rounds.setup')}
        </Button>
      </HeaderActions>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div className="space-y-1.5">
            <CardTitle className="flex flex-wrap items-center gap-2">
              {round.name}
              <Badge variant={STATUS_VARIANT[round.status]} data-testid="round-status">
                {t(`gymAdmin.rounds.status.${round.status}`)}
              </Badge>
              {/* A turn's board holds the people who booked it and those who
                  booked none — said here so a short list does not look wrong. */}
              {round.bookable && (
                <Badge variant="outline" className="font-normal" data-testid="round-bookable-badge">
                  {round.max_participants
                    ? t('gymAdmin.rounds.bookable.badgeLimited', {
                        count: round.seats_taken ?? round.bookings_count ?? 0,
                        max: round.max_participants,
                      })
                    : t('gymAdmin.rounds.bookable.badge', { count: round.bookings_count ?? 0 })}
                </Badge>
              )}

              {/* The round's clock, beside the state it is in. The day's
                  decisions are taken here, with the board open. */}
              <ToggleGroup
                type="single"
                value={round.status}
                // Radix reports an empty value when the pressed item is pressed
                // again; a round is always somewhere, so that is a no-op.
                onValueChange={(next) => next && requestStatus(next as RoundStatus)}
                aria-label={t('gymAdmin.rounds.statusLabel')}
                // Why half of it is dead, for whoever goes looking.
                title={t('gymAdmin.rounds.statusHelp')}
                className="inline-flex h-8 items-center gap-1 rounded-md border bg-muted/40 p-0.5 print:hidden"
                data-testid="round-status-toggle"
              >
                {ROUND_STATUSES.map((status) => (
                  <ToggleGroupItem
                    key={status}
                    value={status}
                    // Only the doors that would actually open, plus the state it
                    // is already in — which is where it is, not a move.
                    disabled={!!busy || (status !== round.status && !available.has(status))}
                    data-testid={`round-status-${status}`}
                    className="inline-flex h-7 items-center rounded px-2 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-40 data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm"
                  >
                    {t(`gymAdmin.rounds.status.${status}`)}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
              {busy && <Loader2 className="size-4 animate-spin text-muted-foreground" aria-hidden />}

              {round.running && round.seconds_remaining !== null && (
                <RoundCountdown
                  secondsRemaining={round.seconds_remaining}
                  onExpire={() => slug && roundId && load(slug, roundId)}
                />
              )}
              {!round.running && (
                <Badge variant="outline" className="gap-1 font-normal">
                  <Timer className="size-3" />
                  {round.duration_minutes
                    ? t('competitionRounds.plannedDuration', { count: round.duration_minutes })
                    : t('competitionRounds.noDuration')}
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              {round.round_type} • {t('competitionRounds.climbsCount', { count: climbs.length })}
              <span className="mt-1 block print:hidden">
                {/* A draft with nothing on the wall cannot be started at all,
                    and a closed round takes nothing from anybody: the two
                    refusals worth explaining before they are met. Otherwise:
                    what the table below does, which is not the same sentence
                    for the two ways of reading it. */}
                {round.status === 'draft' && !round.can_start
                  ? t('gymAdmin.rounds.needsBlocks')
                  : !scoringOpen
                    ? t('gymAdmin.roundScores.closedBoard')
                    : view === 'blocks'
                      ? t(
                          isLead
                            ? 'gymAdmin.roundBlocks.routesTableDescription'
                            : 'gymAdmin.roundBlocks.tableDescription'
                        )
                      : t('gymAdmin.roundScores.description')}
              </span>
            </CardDescription>
          </div>
          {/* Prints the open class only — one sheet per tab, which is how a
              results sheet is posted anyway. */}
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 gap-2"
            // Whatever is on screen is what goes on paper, so there is nothing
            // to print when the open class is empty. The by-block table can
            // still have rows when the entrant filter matches nobody — a typed
            // block name keeps its whole field — so it is asked about the
            // class, not about the filtered-down list of names.
            disabled={
              categories.length === 0 ||
              (view === 'athletes' ? visible.length === 0 : inTab.length === 0)
            }
            onClick={() => window.print()}
            data-testid="print-scores"
          >
            <Printer className="size-4" />
            {t('gymAdmin.roundScores.print')}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {categories.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t('gymAdmin.roundScores.noCategoriesLinked')}
            </p>
          ) : (
            <>
              {/* Which way the table reads, one tab per class the round runs,
                  and the filter beside them. All three live in the URL. */}
              <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
                <div className="flex flex-wrap items-center gap-3">
                  <ToggleGroup
                    type="single"
                    value={view}
                    // Radix reports an empty value when the pressed item is
                    // pressed again; the table always reads one way or the
                    // other, so that is a no-op.
                    onValueChange={(next) => next && setView(next)}
                    aria-label={t('gymAdmin.roundScores.view.label')}
                    className="inline-flex h-9 items-center gap-1 rounded-md border bg-muted/40 p-0.5"
                    data-testid="board-view-toggle"
                  >
                    {BOARD_VIEWS.map(({ value, icon: Icon }) => (
                      <ToggleGroupItem
                        key={value}
                        value={value}
                        data-testid={`board-view-${value}`}
                        className="inline-flex h-8 items-center gap-1.5 rounded px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm"
                      >
                        <Icon className="size-3.5" aria-hidden />
                        {/* The other way of reading the round is "by block" or
                            "by route" depending on what the round is made of. */}
                        {t(
                          value === 'blocks' && isLead
                            ? 'gymAdmin.roundScores.view.routes'
                            : `gymAdmin.roundScores.view.${value}`
                        )}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>

                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                      {categories.map((category) => (
                        <TabsTrigger key={category.id} value={category.id}>
                          {category.name}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </Tabs>

                  {/* A turn's rank is this turn's only: the class's standings
                      across all its turns are a page of their own, read rather
                      than written on, and this is the way there with the class
                      already open. */}
                  {round.bookable && openCategory && (
                    <Link
                      to={placePath(`/standings?category=${encodeURIComponent(openCategory.id)}`)}
                      className="text-sm font-medium text-primary underline-offset-4 hover:underline"
                      data-testid="category-standings-link"
                    >
                      {t('gymAdmin.standings.roundLink', {
                        category: openCategory.name,
                        count: openCategory.turns_count ?? 1,
                      })}
                    </Link>
                  )}
                </div>
                <ScoreboardFilter
                  value={filter}
                  onChange={setFilter}
                  // By block a typed word is matched against the problems too,
                  // so the box says so rather than promising less than it does.
                  placeholder={
                    view === 'blocks'
                      ? t('gymAdmin.roundBlocks.filterPlaceholder')
                      : t('gymAdmin.roundScores.filterPlaceholder')
                  }
                  className="w-full sm:w-72"
                />
              </div>

              {/* Everything from here down is what the print button puts on
                  paper, and nothing above it. */}
              <div data-print-area className="space-y-4">
                {/* A sheet has to say what it is once it has left the screen. */}
                <div className="hidden print:block">
                  <h1 className="text-base font-semibold">
                    {competition.name} — {round.name}
                  </h1>
                  <p className="text-xs">
                    {openCategory?.name}
                    {openCategory && ' • '}
                    {new Date().toLocaleDateString(i18n.language)}
                  </p>
                </div>

                {/* By athlete: one row per entrant, their whole card behind
                    a click. */}
                {view === 'athletes' && (
                  <div className="overflow-x-auto">
                    <table className={cn('w-full text-sm', startList && 'print:table-fixed')}>
                      <thead className="border-b text-xs text-muted-foreground">
                        <tr>
                          {/* Two narrow columns for one name is a screen habit: on
                              paper it eats the half of the sheet the judge writes
                              on, so the name prints as one. */}
                          <th
                            className={cn(
                              'hidden px-3 py-2 text-left font-medium print:table-cell',
                              startList && 'print:w-[32%]'
                            )}
                            data-testid="print-athlete-header"
                          >
                            {t('gymAdmin.roundScores.athlete')}
                          </th>
                          {sortableHeader('name', t('gymAdmin.members.fields.name'), {
                            className: 'print:hidden',
                          })}
                          {sortableHeader('surname', t('gymAdmin.members.fields.surname'), {
                            className: 'print:hidden',
                          })}
                          {sortableHeader('team', t('gymAdmin.members.fields.team'), {
                            className: startList ? 'print:w-[18%]' : undefined,
                          })}
                          <th className={cn('px-3 py-2 text-left font-medium', resultColumn)}>
                            {t(isLead ? 'leadRounds.card' : 'gymAdmin.roundScores.solved')}
                          </th>
                          {ranked &&
                            sortableHeader('rank', t('gymAdmin.roundScores.rank'), {
                              // The rule of the regulation in force.
                              title: isLead
                                ? t('leadRounds.rankHelp')
                                : t(`competitionRankings.rankHelp.${ranking.strategy}`, {
                                    defaultValue: t('competitionRankings.rankHelp.standard'),
                                  }),
                              className: resultColumn,
                            })}
                          {/* The half of a start list the judge fills in by hand. */}
                          {startList && <th className="hidden print:table-cell print:w-1/2" />}
                        </tr>
                      </thead>
                      <tbody>
                        {visible.length === 0 && (
                          <tr>
                            <td
                              colSpan={ranked ? 5 : 4}
                              className="px-3 py-8 text-center text-muted-foreground"
                            >
                              {/* Nobody at all is a different message from
                                  nobody matching what was typed. */}
                              {inTab.length === 0
                                ? t('gymAdmin.roundScores.empty')
                                : t('gymAdmin.roundScores.noMatch')}
                            </td>
                          </tr>
                        )}
                        {visible.map((score) => {
                          return (
                            <tr
                              key={score.id}
                              className="border-b last:border-0"
                              data-testid="score-row"
                            >
                              <td
                                className="hidden px-3 py-2 print:table-cell"
                                data-testid="print-athlete"
                              >
                                {[score.name, score.surname].filter(Boolean).join(' ')}
                              </td>
                              <td className="px-3 py-2 print:hidden" data-testid="cell-name">
                                {score.name}
                              </td>
                              <td className="px-3 py-2 print:hidden" data-testid="cell-surname">
                                {score.surname}
                              </td>
                              <td className="px-3 py-2 text-muted-foreground">
                                {score.team_name ?? '—'}
                              </td>
                              <td className={cn('px-3 py-2', resultColumn)}>
                                {/* What they solved, and the way into correcting it.
                                    A boulder round is counted — tops and zones, a
                                    top counting as a zone too. Anything else has
                                    no such marks, so it stays the card-by-card
                                    shorthand. */}
                                <button
                                  type="button"
                                  onClick={() => setEditing(score)}
                                  className="flex flex-wrap items-center gap-1 rounded px-1 py-0.5 hover:bg-muted"
                                  data-testid="open-score-dialog"
                                >
                                  {isLead ? (
                                    // A lead round: the hold reached on each
                                    // route and how long it took. Every route in
                                    // a row, which is what the geometric mean is
                                    // taken over.
                                    <span
                                      className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
                                      data-testid="lead-tally"
                                    >
                                      {score.cards.map((card) => (
                                        <LeadCardSummary
                                          key={card.round_climb_id}
                                          holds={card.holds_count}
                                          timeSeconds={card.time_seconds}
                                          formattedTime={card.formatted_time}
                                          topped={card.topped}
                                          climbedAs={card.climbed_as}
                                          label={card.climb_name}
                                          className="text-xs"
                                        />
                                      ))}
                                    </span>
                                  ) : isBoulder ? (
                                    <BoulderTally
                                      tally={tally(score.cards, judged)}
                                      showJudged={ranking.judgedBlocks}
                                      attempts={rankingAttempts(score.score)}
                                    />
                                  ) : (
                                    score.cards.map((card) => (
                                      <Badge
                                        key={card.round_climb_id}
                                        variant="outline"
                                        className="font-mono text-xs font-normal"
                                      >
                                        {summarise(card.attempts.map((a) => a.attempt_type))}
                                      </Badge>
                                    ))
                                  )}
                                </button>
                              </td>
                              {/* Where they stand in their class. Nobody who has yet
                                  to be judged on anything gets a position — early in
                                  a round that would be the whole field, all first. */}
                              {ranked && (
                                <td
                                  className={cn('px-3 py-2 font-semibold tabular-nums', resultColumn)}
                                  data-testid="score-rank"
                                >
                                  {places.get(score.id) != null ? (
                                    <span className="inline-flex items-center gap-1.5">
                                      {places.get(score.id)}
                                      <TiebreakMark tiebreak={score.tiebreak} />
                                    </span>
                                  ) : (
                                    <span className="font-normal text-muted-foreground">—</span>
                                  )}
                                </td>
                              )}
                              {/* Empty on purpose: the rule under it is the line
                                  the judge writes the attempts on, and the height
                                  is the room to write them in. */}
                              {startList && (
                                <td
                                  className="hidden print:table-cell print:h-10"
                                  data-testid="print-notes-cell"
                                />
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* By block: one problem per field, everybody on it, and
                    the marks going on as each go happens. The same table a
                    judge gets on `/blocks` — it is the round transposed, not
                    a second way of scoring it. */}
                {view === 'blocks' &&
                  (isLead ? (
                    <RoundRoutesTable
                      competitionSlug={slug}
                      roundId={round.id}
                      scores={scores}
                      climbs={climbs}
                      judges={judges}
                      categoryId={activeTab}
                      classStyles={classStyles}
                      filter={filter}
                      canSetBlocks={canSetBlocks}
                      canScore={scoringOpen}
                      onScoreSaved={(saved) => setScores((current) => withSavedScore(current, saved))}
                      onClimbChanged={(saved) =>
                        setClimbs((current) => current.map((c) => (c.id === saved.id ? saved : c)))
                      }
                      onRoundClosed={refreshRound}
                    />
                  ) : (
                    <RoundBlocksTable
                      competitionSlug={slug}
                      roundId={round.id}
                      scores={scores}
                      climbs={climbs}
                      judges={judges}
                      categoryId={activeTab}
                      filter={filter}
                      canSetBlocks={canSetBlocks}
                      canScore={scoringOpen}
                      judgedBlocks={ranking.judgedBlocks}
                      onScoreSaved={(saved) => setScores((current) => withSavedScore(current, saved))}
                      onClimbChanged={(saved) =>
                        setClimbs((current) => current.map((c) => (c.id === saved.id ? saved : c)))
                      }
                      onRoundClosed={refreshRound}
                    />
                  ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* One athlete's whole card. Which dialog is the discipline's: presses on a
          boulder round, two typed numbers per route on a lead one. */}
      {isLead ? (
        <LiveLeadCardDialog
          open={editing !== null}
          onOpenChange={(next) => !next && setEditing(null)}
          competitionSlug={slug}
          roundId={round.id}
          score={editing}
          climbs={climbs}
          classStyles={classStyles}
          canScore={scoringOpen}
          onClosed={(latest) => setScores((current) => withSavedScore(current, latest))}
          onRoundClosed={refreshRound}
        />
      ) : (
        <LiveScoreCardDialog
          open={editing !== null}
          onOpenChange={(next) => !next && setEditing(null)}
          competitionSlug={slug}
          roundId={round.id}
          score={editing}
          canScore={scoringOpen}
          judgedBlocks={ranking.judgedBlocks}
          onClosed={(latest) => setScores((current) => withSavedScore(current, latest))}
          onRoundClosed={refreshRound}
        />
      )}

      <StartRoundDialog
        open={startOpen}
        onOpenChange={setStartOpen}
        round={round}
        onConfirm={(minutes) =>
          runAction('start', () => competitionRoundsService.startRound(slug, round.id, minutes))
        }
      />

      {/* The round would not start, and this is what somebody has to go and do
          about it — the routes still waiting to have their holds counted, by
          name, with the way to the page where they are entered. */}
      <Dialog open={startRefusal !== null} onOpenChange={(next) => !next && setStartRefusal(null)}>
        <DialogContent className="sm:max-w-md" data-testid="start-refused-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="size-5 text-destructive" aria-hidden />
              {t('leadRounds.cannotStartTitle')}
            </DialogTitle>
            <DialogDescription>{startRefusal}</DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button variant="outline" onClick={() => setStartRefusal(null)}>
              {t('common.close')}
            </Button>
            <Button
              onClick={() => {
                setStartRefusal(null);
                navigate(placePath(`/rounds/${round.id}/edit`));
              }}
              data-testid="go-to-round-setup"
            >
              <Settings2 className="mr-2 size-4" aria-hidden />
              {t('gymAdmin.rounds.setup')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Going back to not-started asks every time, because neither answer about
          the results is safe to assume — and the box is unticked, so the
          destructive one is never the default. */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('gymAdmin.rounds.reset')}</DialogTitle>
            <DialogDescription>{t('gymAdmin.rounds.resetDescription')}</DialogDescription>
          </DialogHeader>

          <label className="flex items-start gap-3 rounded-lg border p-3 text-sm">
            <Checkbox
              checked={clearResults}
              onCheckedChange={(value) => setClearResults(value === true)}
              data-testid="reset-clear-results"
            />
            <span>
              <span className="font-medium">{t('gymAdmin.rounds.resetClearResults')}</span>
              <span className="block text-xs text-muted-foreground">
                {t('gymAdmin.rounds.resetClearResultsHelp')}
              </span>
            </span>
          </label>

          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant={clearResults ? 'destructive' : 'default'}
              disabled={busy === 'reset'}
              onClick={async () => {
                await runAction('reset', () =>
                  competitionRoundsService.resetRound(slug, round.id, clearResults)
                );
                setResetOpen(false);
                setClearResults(false);
              }}
              data-testid="confirm-reset-round"
            >
              {busy === 'reset' && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t('gymAdmin.rounds.reset')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* To the check, or back to the athletes: who may write changes. */}
      <Dialog open={statusMove !== null} onOpenChange={(open) => !open && setStatusMove(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {statusMove === 'verification' ? t('gymAdmin.rounds.verify') : t('gymAdmin.rounds.reopen')}
            </DialogTitle>
            <DialogDescription>
              {statusMove === 'verification'
                ? t('gymAdmin.rounds.verifyDescription')
                : t('gymAdmin.rounds.reopenDescription')}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusMove(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              disabled={busy === 'status'}
              onClick={async () => {
                if (!statusMove) return;
                await runAction('status', () =>
                  competitionRoundsService.updateRoundStatus(slug, round.id, statusMove)
                );
                setStatusMove(null);
              }}
              data-testid="confirm-round-status"
            >
              {busy === 'status' && <Loader2 className="mr-2 size-4 animate-spin" />}
              {statusMove === 'verification' ? t('gymAdmin.rounds.verify') : t('gymAdmin.rounds.reopen')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nothing in the member app writes on the round after this, so it says so
          before it happens. Corrections stay possible from this board. */}
      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('gymAdmin.rounds.close')}</DialogTitle>
            <DialogDescription>{t('gymAdmin.rounds.closeDescription')}</DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              disabled={busy === 'close'}
              onClick={async () => {
                await runAction('close', () => competitionRoundsService.closeRound(slug, round.id));
                setCloseOpen(false);
              }}
              data-testid="confirm-close-round"
            >
              {busy === 'close' && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t('gymAdmin.rounds.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
